import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { LANGS } from './NewsletterFormsCard.jsx';
import { ItemRow, Detail, Sections, Metrics, Glance, EditLayout, PreviewPane, PromoPreview, SaveBar } from './website/HubKit.jsx';

// Website pop-ups — promotion & announcement creatives that pop up on the
// client's OWN website (server/sitePromos.js, rendered by
// client/public/howler-promo.js).
//
// Dual-surface via `scope`:
//   'admin-client' → Admin → the client → Website pop-ups
//   'my'           → Settings → Website pop-ups
//
// The site code goes on the website once ("Your website code"); every
// pop-up after that is built, scheduled and paused here. Preview renders the
// unsaved draft with the SAME script the website runs, over this page.
//
// Mobile-first: one stacked column, ≥40px controls.
const BLANK = {
  name: '', status: 'active', priority: 0, startsAt: '', endsAt: '', linkHash: '', defaultLang: 'en', translations: {},
  blocks: [],
  look: { size: 'medium', position: 'center', bg: '#ffffff', fg: '#16181d', accent: '', align: 'center', overlay: 55, closeOnBackdrop: true },
  trigger: { manual: false, delay: 3, scroll: 0, pageviews: 0, exit: false },
  frequency: { mode: 'days', days: 7 },
  targeting: { include: [], exclude: [], device: 'all' },
};
const BLOCKS = [
  ['image', 'Image'], ['video', 'Video'], ['heading', 'Heading'], ['text', 'Text'],
  ['button', 'Button'], ['countdown', 'Countdown'], ['signup', 'Sign-up form'], ['html', 'Your own HTML'],
];
const BLOCK_NAME = Object.fromEntries(BLOCKS);
const newBlock = (type) => ({
  id: Math.random().toString(16).slice(2, 12).padEnd(10, '0'), type,
  ...(type === 'image' ? { src: '', alt: '', link: '', edge: true } : {}),
  ...(type === 'video' ? { url: '', autoplay: false } : {}),
  ...(type === 'heading' || type === 'text' ? { text: '' } : {}),
  ...(type === 'button' ? { label: '', url: '', style: 'solid', newTab: false } : {}),
  ...(type === 'countdown' ? { to: '', label: 'Offer ends in', after: '' } : {}),
  ...(type === 'signup' ? { form: '', mode: 'button', label: 'Register', compact: false, ownHeading: false } : {}),
  ...(type === 'html' ? { html: '', height: 0 } : {}),
});
const STATE = { live: '● Live', scheduled: '◷ Scheduled', ended: '✓ Ended', paused: '⏸ Paused' };
const FREQ_LABEL = { once: 'Only once', days: 'Once every few days', session: 'Once per visit', always: 'Every page they open' };

export default function SitePromosCard({ entityId, scope = 'my' }) {
  const apiScope = scope === 'my' ? 'my' : 'admin';
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // Where we are: the list, one pop-up (and which view), or a new one.
  const [view, setView] = useState({ mode: 'list', id: '', tab: 'overview' });
  const [draft, setDraft] = useState(BLANK);
  const [q, setQ] = useState('');

  const load = useCallback(() => api.sitePromos(entityId, apiScope)
    .then((v) => { if (v && v.error) throw new Error(v.error); setData(v); })
    .catch(() => setDenied(true)), [entityId, apiScope]);
  useEffect(() => { load(); }, [load]);

  if (denied) return null; // flag off / no permission — the section simply isn't there
  if (!data) return <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading website pop-ups…</div>;
  const promos = data.promos || [];

  const run = async (key, fn) => {
    setBusy(key); setErr('');
    try {
      const v = await fn();
      if (v && v.error) { setErr(v.error); return false; }
      if (v && v.promos) setData(v);
      return v || true;
    } catch (e) { setErr(String(e.message || e)); return false; }
    finally { setBusy(''); }
  };
  const open = (p, tab = 'overview') => { setDraft({ ...BLANK, ...p }); setErr(''); setView({ mode: 'item', id: p.id, tab }); };
  const startNew = () => { setDraft({ ...BLANK, blocks: [newBlock('image'), newBlock('heading'), newBlock('text'), newBlock('button')] }); setErr(''); setView({ mode: 'new', id: '', tab: 'edit' }); };
  const back = () => { setErr(''); setView({ mode: 'list', id: '', tab: 'overview' }); };
  const save = async () => {
    const r = await run('save', () => (view.mode === 'new'
      ? api.sitePromoCreate(entityId, apiScope, draft)
      : api.sitePromoUpdate(entityId, apiScope, view.id, draft)));
    if (!r) return;
    const id = view.mode === 'new' ? r.id : view.id;
    const p = (r.promos || []).find((x) => x.id === id) || (view.mode === 'new' ? (r.promos || []).find((x) => x.name === draft.name) : null);
    if (p) open(p, 'overview'); else back();
  };
  const signup = { forms: data.forms || [], on: !!data.newsletterOn, reload: load };

  // ── the list ───────────────────────────────────────────────────────────────
  if (view.mode === 'list') {
    const needle = q.trim().toLowerCase();
    const shown = promos.filter((p) => !needle || p.name.toLowerCase().includes(needle));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ ...hint, margin: 0 }}>
          Promotions and announcements that pop up on <b>your own website</b>. With the site code on your site (Site settings),
          every pop-up is made, scheduled and swapped here, and your site follows straight away.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {promos.length > 3 && <input type="search" style={{ ...input, flex: '1 1 240px' }} placeholder="Search pop-ups" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search pop-ups" />}
          <button type="button" style={{ ...btn, ...primary }} onClick={startNew}>+ New pop-up</button>
        </div>
        {promos.length === 0 && <div style={okBox}>No pop-ups yet. Create one, check it in the preview, then add the site code to your website.</div>}
        {shown.map((p) => (
          <ItemRow key={p.id} name={p.name} status={STATE[p.state] || p.state} tone={p.state === 'live' ? 'live' : p.state === 'scheduled' ? 'scheduled' : 'paused'}
            summary={summary(p)}
            stats={[{ value: p.stats.views.toLocaleString(), label: 'views' }, { value: `${p.stats.ctr}%`, label: 'click rate' }]}
            onOpen={() => open(p)} />
        ))}
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // ── one pop-up (or a new one) ─────────────────────────────────────────────
  const p = promos.find((x) => x.id === view.id);
  const isNew = view.mode === 'new';
  if (!isNew && !p) { back(); return null; }
  const editor = (
    <EditLayout
      editor={(
        <>
          <Editor draft={draft} setDraft={setDraft} isNew={isNew} entityId={entityId} apiScope={apiScope} signup={signup} />
          <SaveBar onSave={save} onCancel={isNew ? back : () => open(p, 'overview')} busy={busy === 'save'} isNew={isNew} createLabel="Create pop-up" error={err} />
        </>
      )}
      preview={<PromoPreviewPane promo={draft} entityId={entityId} apiScope={apiScope} />}
    />
  );
  if (isNew) return <Detail backLabel="Pop-ups" onBack={back} name={draft.name || 'New pop-up'}>{editor}</Detail>;
  const hasSignup = p.blocks.some((b) => b.type === 'signup');
  return (
    <Detail backLabel="Pop-ups" onBack={back} name={p.name} status={STATE[p.state] || p.state} tone={p.state === 'live' ? 'live' : p.state === 'scheduled' ? 'scheduled' : 'paused'}
      actions={[
        { label: 'Duplicate', disabled: !!busy, onClick: () => run(`dup-${p.id}`, () => api.sitePromoDuplicate(entityId, apiScope, p.id)).then((ok) => ok && back()) },
        { label: p.status === 'paused' ? 'Resume' : 'Pause', disabled: !!busy, onClick: () => run(`st-${p.id}`, () => api.sitePromoUpdate(entityId, apiScope, p.id, { status: p.status === 'paused' ? 'active' : 'paused' })) },
        { label: 'Delete', danger: true, disabled: !!busy, onClick: async () => { if (window.confirm(`Delete “${p.name}”? It disappears from your website straight away.`) && await run(`rm-${p.id}`, () => api.sitePromoRemove(entityId, apiScope, p.id))) back(); } },
      ]}
      tabs={[['overview', 'Overview'], ['code', 'Add to website'], ['edit', 'Edit']]}
      tab={view.tab} onTab={(tab) => { if (tab === 'edit') setDraft({ ...BLANK, ...p }); setView({ ...view, tab }); }}>
      {view.tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Metrics items={[
            { value: p.stats.views.toLocaleString(), label: 'Views', hint: 'Times it opened' },
            { value: p.stats.clicks.toLocaleString(), label: 'Clicks', hint: 'On its buttons and image' },
            { value: `${p.stats.ctr}%`, label: 'Click rate', hint: 'Clicks ÷ views' },
            { value: p.stats.closes.toLocaleString(), label: 'Closed', hint: 'Without clicking' },
            hasSignup && { value: (p.stats.signups || 0).toLocaleString(), label: 'Sign-ups', hint: 'Through its sign-up form' },
          ]} />
          <Glance rows={[
            ['Content', (p.blocks || []).map((b) => BLOCK_NAME[b.type] || b.type).join(' · ') || 'Empty'],
            ['When it opens', p.trigger.manual ? `From links to #${p.linkHash || 'promo'}` : openWhen(p.trigger)],
            ['How often', FREQ_LABEL[p.frequency.mode] + (p.frequency.mode === 'days' ? ` (every ${p.frequency.days} days)` : '')],
            ['Where', `${(p.targeting.include || []).length ? p.targeting.include.join(', ') : 'Every page'}${(p.targeting.exclude || []).length ? `, not ${p.targeting.exclude.join(', ')}` : ''} · ${{ all: 'phones and computers', mobile: 'phones only', desktop: 'computers only' }[p.targeting.device] || 'all devices'}`],
            ['Dates', [p.startsAt && `from ${fmt(p.startsAt)}`, p.endsAt && `until ${fmt(p.endsAt)}`].filter(Boolean).join(' ') || 'No dates: live while it’s on'],
            ['Look', `${p.look.position === 'corner' ? 'Corner card' : `Pop-up, ${p.look.size}`} · corners ${p.look.radius ?? 16}px${p.look.buttonStyle === 'caps' ? ' · uppercase buttons' : ''}`],
            ['Languages', [LANGS[p.defaultLang] || p.defaultLang, ...Object.keys(p.translations || {}).map((l) => LANGS[l] || l)].join(', ')],
          ]} />
          <PromoPreviewPane promo={p} entityId={entityId} apiScope={apiScope} />
        </div>
      )}
      {view.tab === 'code' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SiteCode data={data} />
          <div style={label}>Or pin this pop-up on one page</div>
          <pre style={code}>{p.embedSnippet}</pre>
          <div style={hint}>For a page that should always show this one, whatever else is live. Most sites only need the site code above.</div>
        </div>
      )}
      {view.tab === 'edit' && editor}
      {view.tab !== 'edit' && err && <div style={errBox}>{err}</div>}
    </Detail>
  );
}

function PromoPreviewPane({ promo, entityId, apiScope }) {
  const langs = [promo.defaultLang || 'en', ...Object.keys(promo.translations || {})];
  const [lang, setLang] = useState(langs[0]);
  const cur = langs.includes(lang) ? lang : langs[0];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <PreviewPane langs={langs.map((l) => [l, LANGS[l] || l])} lang={cur} onLang={setLang} note="Drawn by the same code your website runs, from your unsaved changes.">
        <PromoPreview entityId={entityId} apiScope={apiScope} promo={promo} lang={cur} />
      </PreviewPane>
      <div><PreviewButton promo={promo} entityId={entityId} apiScope={apiScope} label="Open full size over this page" lang={cur} /></div>
    </div>
  );
}

// Exported for Admin/Settings → Website → Site settings.
export { SiteCode };

function SiteCode({ data }) {
  const [copied, setCopied] = useState('');
  const copy = (k, v) => { try { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); } catch { /* select manually */ } };
  return (
    <div style={panel}>
      <div style={hint}>
        Paste this <b>once</b>, in your site’s header or footer code so it is on every page (WordPress: a header/footer
        plugin or Custom HTML block · Webflow: Site settings → Custom code · Wix: Settings → Custom code · Squarespace:
        Code injection). It shows nothing until one of your pop-ups is live.
      </div>
      <pre style={code}>{data.siteSnippet}</pre>
      <button type="button" style={btn} onClick={() => copy('site', data.siteSnippet)}>{copied === 'site' ? '✓ Copied' : 'Copy code'}</button>
      <div style={{ ...hint, marginTop: 10 }}>
        <b>Open it from your own button or text:</b> set the link of any button, menu item or text on your site to{' '}
        <code>#promo</code>. Clicking it opens the current pop-up, and sharing <code>yoursite.com/#promo</code> opens it
        straight away. Give a pop-up its own link (under Edit → When it opens) to open that one in particular.
      </div>
      <div style={{ ...hint, marginTop: 4 }}>
        More than one pop-up live on the same page? Visitors see one per page view: the one with the highest priority.
      </div>
    </div>
  );
}

// Shows the pop-up over this page, with the same script the website runs.
function loadRenderer() {
  if (window.customElements && customElements.get('howler-promo')) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = '/howler-promo.js'; s.onload = () => resolve(); s.onerror = reject;
    document.head.appendChild(s);
  });
}
async function showPreview(entityId, apiScope, promo, lang = '') {
  const r = await api.sitePromoPreview(entityId, apiScope, promo, lang);
  if (r.error) throw new Error(r.error);
  await loadRenderer();
  let el = document.querySelector('howler-promo[data-editor]');
  if (!el) { el = document.createElement('howler-promo'); el.setAttribute('data-editor', ''); document.body.appendChild(el); }
  el.show(r.promo, { force: true, quiet: true });
}
function PreviewButton({ promo, entityId, apiScope, label = 'Preview', style, lang = '' }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try { await showPreview(entityId, apiScope, promo, lang); } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };
  return (
    <>
      <button type="button" style={{ ...btn, ...style }} disabled={busy} onClick={go}>{busy ? 'Opening…' : label}</button>
      {err && <span style={errBox}>{err}</span>}
    </>
  );
}

function Editor({ draft, setDraft, isNew, entityId, apiScope, signup }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setIn = (group, k, v) => setDraft((d) => ({ ...d, [group]: { ...(d[group] || {}), [k]: v } }));
  const look = draft.look || BLANK.look;
  const tr = draft.trigger || BLANK.trigger;
  const freq = draft.frequency || BLANK.frequency;
  const tg = draft.targeting || BLANK.targeting;
  const blocks = draft.blocks || [];
  const langs = Object.keys(draft.translations || {});
  return (
    <Sections initial={isNew ? ['basics', 'content'] : []} list={[
      { id: 'basics', title: 'Name', summary: draft.name || 'Untitled', content: (
        <label style={label}>Name (only you see this)
          <input style={input} value={draft.name || ''} placeholder="e.g. Early bird ends Friday" maxLength={120} onChange={(e) => set('name', e.target.value)} />
        </label>
      ) },
      { id: 'content', title: 'Content', summary: blocks.length ? blocks.map((b) => BLOCK_NAME[b.type] || b.type).join(' · ') : 'Empty: add an image, text or a button', content: (
        <>
          <div style={{ ...hint, marginTop: 8 }}>Top to bottom. Move any block up or down: the preview follows.</div>
          <BlocksEditor blocks={blocks} onChange={(v) => set('blocks', v)} entityId={entityId} apiScope={apiScope} signup={signup} promoName={draft.name} />
        </>
      ) },
      { id: 'look', title: 'Look', summary: `${look.position === 'corner' ? 'Corner card' : `Pop-up, ${look.size}`} · corners ${look.radius ?? 16}px${look.buttonStyle === 'caps' ? ' · uppercase buttons' : ''}`, content: (
          <div style={subRow}>
            <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Shape
              <select style={input} value={look.position} onChange={(e) => setIn('look', 'position', e.target.value)}>
                <option value="center">Pop-up in the middle of the screen</option>
                <option value="corner">Small card in the bottom corner (doesn’t cover the page)</option>
              </select>
            </label>
            {look.position !== 'corner' && (
              <label style={{ ...label, fontWeight: 400 }}>Width
                <select style={input} value={look.size} onChange={(e) => setIn('look', 'size', e.target.value)}>
                  <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large (good for a big image or video)</option>
                </select>
              </label>
            )}
            <label style={{ ...label, fontWeight: 400 }}>Text size
              <select style={input} value={look.textSize || 'normal'} onChange={(e) => setIn('look', 'textSize', e.target.value)}>
                <option value="normal">Normal</option><option value="large">Large</option><option value="xl">Extra large</option>
              </select>
            </label>
            <label style={{ ...label, fontWeight: 400 }}>Text alignment
              <select style={input} value={look.align} onChange={(e) => setIn('look', 'align', e.target.value)}>
                <option value="center">Centred</option><option value="left">Left</option>
              </select>
            </label>
            <Colour label="Background" value={look.bg} onChange={(v) => setIn('look', 'bg', v)} fallback="#ffffff" />
            <Colour label="Text" value={look.fg} onChange={(v) => setIn('look', 'fg', v)} fallback="#16181d" />
            <Colour label="Buttons" value={look.accent} onChange={(v) => setIn('look', 'accent', v)} fallback="#111111" blankLabel="Your branding colour" />
            <Num label="Corners of the pop-up (0 = square)" value={look.radius ?? 16} max={40} unit="px" onChange={(v) => setIn('look', 'radius', v)} />
            <Num label="Corners of the buttons" value={look.buttonRadius ?? 10} max={30} unit="px" onChange={(v) => setIn('look', 'buttonRadius', v)} />
            <label style={{ ...label, fontWeight: 400 }}>Buttons style
              <select style={input} value={look.buttonStyle || 'normal'} onChange={(e) => setIn('look', 'buttonStyle', e.target.value)}>
                <option value="normal">Standard</option><option value="caps">UPPERCASE with an arrow ↗</option>
              </select>
            </label>
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 12.5, cursor: 'pointer', minHeight: 32 }}>Custom CSS (advanced)</summary>
              <textarea style={{ ...input, minHeight: 90, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, marginTop: 6 }} value={look.customCss || ''} maxLength={10000}
                placeholder={'h2 { letter-spacing: -0.02em; }'} onChange={(e) => setIn('look', 'customCss', e.target.value)} aria-label="Custom CSS" />
              <div style={{ ...hint, margin: '4px 0 0' }}>
                Applies to this pop-up only, never the rest of the website. Classes: <code>.modal</code> (the pop-up), <code>.body</code>, <code>h2</code>,
                <code>p.t</code> (text), <code>.img</code>, <code>.btns</code> / <code>a.b</code> (buttons), <code>.cd</code> (countdown), <code>button.x</code> (close).
                Only https:// links are allowed in <code>url()</code>.
              </div>
            </details>
            {look.position !== 'corner' && (
              <>
                <Num label="Darken the page behind it" value={look.overlay} max={90} unit="%" onChange={(v) => setIn('look', 'overlay', v)} />
                <label style={{ ...chkRow, margin: '6px 0' }}><input type="checkbox" style={chk} checked={look.closeOnBackdrop !== false} onChange={(e) => setIn('look', 'closeOnBackdrop', e.target.checked)} /> Close it when they tap outside it</label>
              </>
            )}
          </div>
      ) },
      { id: 'opens', title: 'When it opens', summary: tr.manual ? `From links to #${draft.linkHash || 'promo'}` : openWhen(tr), content: (
          <div style={subRow}>
            <label style={{ ...chkRow, margin: '2px 0 6px' }}><input type="checkbox" style={chk} checked={!tr.manual} onChange={(e) => setIn('trigger', 'manual', !e.target.checked)} /> Open by itself</label>
            {!tr.manual ? (
              <>
                <Num label="After" value={tr.delay} max={600} unit="seconds" onChange={(v) => setIn('trigger', 'delay', v)} />
                <Num label="Or once they have scrolled" value={tr.scroll} max={100} unit="% of page" onChange={(v) => setIn('trigger', 'scroll', v)} />
                <label style={{ ...chkRow, margin: '6px 0' }}><input type="checkbox" style={chk} checked={!!tr.exit} onChange={(e) => setIn('trigger', 'exit', e.target.checked)} /> Or when they move to leave the page (computers only)</label>
                <Num label="Wait until the visitor’s page number" value={tr.pageviews} max={50} unit="page" onChange={(v) => setIn('trigger', 'pageviews', v)} />
                <div style={{ ...hint, margin: 0 }}>{openWhen(tr)}</div>
              </>
            ) : (
              <div style={{ ...hint, margin: 0 }}>It only opens from a link or button on your site to <code>#{draft.linkHash || 'promo'}</code>.</div>
            )}
            <label style={{ ...label, fontWeight: 400 }}>Its own link (optional)
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>#</span>
                <input style={input} value={draft.linkHash || ''} placeholder="e.g. earlybird" maxLength={40} onChange={(e) => set('linkHash', e.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase())} />
              </div>
            </label>
            <div style={{ ...hint, margin: 0 }}>A link to <code>#promo</code> always opens your current pop-up; a link to this one’s own name opens this one.</div>
          </div>
      ) },
      { id: 'often', title: 'How often a visitor sees it', summary: FREQ_LABEL[freq.mode] + (freq.mode === 'days' ? ` (every ${freq.days} days)` : ''), content: (
          <div style={subRow}>
            <select style={input} value={freq.mode} onChange={(e) => setIn('frequency', 'mode', e.target.value)}>
              {Object.entries(FREQ_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {freq.mode === 'days' && <Num label="Show it again after" value={freq.days} max={365} unit="days" onChange={(v) => setIn('frequency', 'days', Math.max(1, v))} />}
            <div style={{ ...hint, margin: '6px 0 0' }}>Links to it always open it, however often it has been seen.</div>
          </div>
      ) },
      { id: 'where', title: 'Where', summary: `${(tg.include || []).filter(Boolean).length ? tg.include.filter(Boolean).join(', ') : 'Every page'} · ${{ all: 'phones and computers', mobile: 'phones only', desktop: 'computers only' }[tg.device] || 'all devices'}`, content: (
          <div style={subRow}>
            <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Only on these pages (blank = every page)
              <textarea style={{ ...input, minHeight: 56 }} placeholder={'/\n/tickets*'} value={(tg.include || []).join('\n')} onChange={(e) => setIn('targeting', 'include', e.target.value.split('\n'))} />
            </label>
            <label style={{ ...label, fontWeight: 400 }}>Never on these pages
              <textarea style={{ ...input, minHeight: 44 }} placeholder="/checkout*" value={(tg.exclude || []).join('\n')} onChange={(e) => setIn('targeting', 'exclude', e.target.value.split('\n'))} />
            </label>
            <div style={{ ...hint, margin: '4px 0 0' }}>One per line. <code>/</code> is your home page only, <code>*</code> means “anything”, so <code>/tickets*</code> is every ticket page.</div>
            <label style={{ ...label, fontWeight: 400 }}>Devices
              <select style={input} value={tg.device} onChange={(e) => setIn('targeting', 'device', e.target.value)}>
                <option value="all">Phones and computers</option><option value="mobile">Phones only</option><option value="desktop">Computers only</option>
              </select>
            </label>
          </div>
      ) },
      { id: 'dates', title: 'Dates & priority', summary: [draft.startsAt && `from ${fmt(draft.startsAt)}`, draft.endsAt && `until ${fmt(draft.endsAt)}`].filter(Boolean).join(' ') || `No dates${draft.priority ? ` · priority ${draft.priority}` : ''}`, content: (
          <div style={subRow}>
            <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Starts
              <input type="datetime-local" style={input} value={toLocal(draft.startsAt)} onChange={(e) => set('startsAt', fromLocal(e.target.value))} />
            </label>
            <label style={{ ...label, fontWeight: 400 }}>Ends
              <input type="datetime-local" style={input} value={toLocal(draft.endsAt)} onChange={(e) => set('endsAt', fromLocal(e.target.value))} />
            </label>
            <Num label="Priority (higher wins when two are live)" value={draft.priority || 0} max={100} unit="" onChange={(v) => set('priority', v)} />
          </div>
      ) },
      { id: 'languages', title: 'Languages', summary: [LANGS[draft.defaultLang || 'en'] || draft.defaultLang, ...langs.map((l) => LANGS[l] || l)].join(', '), content: (
        <PromoLanguages draft={draft} setDraft={setDraft} entityId={entityId} apiScope={apiScope} />
      ) },
    ]} />
  );
}

function BlocksEditor({ blocks, onChange, entityId, apiScope, signup, promoName }) {
  const upd = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, d) => { const a = [...blocks]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); onChange(a); };
  return (
    <div>
      {blocks.length === 0 && <div style={hint}>Nothing in it yet. Add an image, a video, text or your own HTML below.</div>}
      {blocks.map((b, i) => (
        <div key={b.id} style={subRow}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={pill}>{BLOCK_NAME[b.type]}</span>
            <span style={{ flex: 1 }} />
            <button type="button" style={miniBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
            <button type="button" style={miniBtn} disabled={i === blocks.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
            <button type="button" style={miniBtn} onClick={() => onChange(blocks.filter((_, j) => j !== i))}>Remove</button>
          </div>
          <BlockFields b={b} first={i === 0} upd={(patch) => upd(i, patch)} entityId={entityId} apiScope={apiScope} signup={signup} promoName={promoName} />
        </div>
      ))}
      <select style={{ ...input, marginTop: 8 }} value="" onChange={(e) => { if (e.target.value) onChange([...blocks, newBlock(e.target.value)]); }} aria-label="Add to the pop-up">
        <option value="">+ Add…</option>
        {BLOCKS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
  );
}

function BlockFields({ b, first, upd, entityId, apiScope, signup, promoName }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const field = (k, ph, opts = {}) => (opts.area
    ? <textarea style={{ ...input, minHeight: opts.tall ? 160 : 64, marginTop: 4, ...(opts.mono ? { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 } : {}) }} value={b[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => upd({ [k]: e.target.value })} aria-label={ph} />
    : <input style={{ ...input, marginTop: 4 }} value={b[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => upd({ [k]: e.target.value })} aria-label={ph} />);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const data = await toDataUrl(file);
      const r = await api.sitePromoImage(entityId, apiScope, data);
      if (r.error) setErr(r.error); else upd({ src: r.src });
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };
  if (b.type === 'image') {
    return (
      <div>
        {b.src && <img src={b.src} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: 160, borderRadius: 8, marginBottom: 6 }} />}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ ...btn, display: 'inline-flex', alignItems: 'center' }}>
            {busy ? 'Uploading…' : b.src ? 'Replace image' : 'Upload image'}
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>or paste an image link:</span>
        </div>
        {field('src', 'https://…/banner.jpg', { max: 1000 })}
        {field('alt', 'Describe the image (for screen readers)', { max: 200 })}
        {field('link', 'Where tapping the image goes (optional): https://…', { max: 1000 })}
        {first && <label style={{ ...chkRow, marginTop: 6 }}><input type="checkbox" style={chk} checked={b.edge !== false} onChange={(e) => upd({ edge: e.target.checked })} /> Run it to the edges (no border around the image)</label>}
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }
  if (b.type === 'video') {
    return (
      <div>
        {field('url', 'YouTube or Vimeo link, or an https link to an .mp4', { max: 1000 })}
        <label style={{ ...chkRow, marginTop: 6 }}><input type="checkbox" style={chk} checked={!!b.autoplay} onChange={(e) => upd({ autoplay: e.target.checked })} /> Play straight away (muted; phones need this to autoplay)</label>
      </div>
    );
  }
  if (b.type === 'heading') return field('text', 'e.g. Early bird tickets end Friday', { max: 200 });
  if (b.type === 'text') return field('text', 'A line or two about the offer. New lines are kept.', { area: true, max: 2000 });
  if (b.type === 'button') {
    return (
      <div>
        {field('label', 'Button text, e.g. Get tickets', { max: 60 })}
        {field('url', 'Link: https://… (or #newsletter to open your sign-up form)', { max: 1000 })}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
          <select style={{ ...input, width: 'auto', flex: '1 1 140px' }} value={b.style} onChange={(e) => upd({ style: e.target.value })} aria-label="Button style">
            <option value="solid">Filled</option><option value="outline">Outline</option>
          </select>
          <label style={{ ...chkRow, margin: 0, alignItems: 'center' }}><input type="checkbox" style={chk} checked={!!b.newTab} onChange={(e) => upd({ newTab: e.target.checked })} /> Open in a new tab</label>
        </div>
        <div style={{ ...hint, margin: '4px 0 0' }}>Buttons next to each other sit side by side.</div>
      </div>
    );
  }
  if (b.type === 'countdown') {
    return (
      <div>
        <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Counts down to (your local time)
          <input type="datetime-local" style={input} value={toLocal(b.to)} onChange={(e) => upd({ to: fromLocal(e.target.value) })} />
        </label>
        {field('label', 'Label above it, e.g. Offer ends in', { max: 120 })}
        {field('after', 'What to show once it reaches zero (optional, blank hides it)', { max: 200 })}
      </div>
    );
  }
  if (b.type === 'signup') return <SignupFields b={b} upd={upd} entityId={entityId} apiScope={apiScope} signup={signup} promoName={promoName} />;
  if (b.type === 'html') {
    return (
      <div>
        {field('html', '<div style="…">Your own creative</div>', { area: true, tall: true, mono: true, max: 60000 })}
        <div style={{ ...hint, margin: '4px 0 0' }}>
          HTML, CSS and scripts from your designer or agency. It runs in its own safe frame, so it can’t affect the rest of your
          website. Links open in the same tab unless they say <code>target="_blank"</code>. Its height adjusts itself.
        </div>
        <Num label="Fixed height (0 = adjust itself)" value={b.height || 0} max={1200} unit="px" onChange={(v) => upd({ height: v })} />
      </div>
    );
  }
  return null;
}

// The pop-up's languages. It is written in its main language; each
// translation is shown to visitors whose browser prefers that language, so a
// site with no language switcher still speaks to each fan in their own. The
// Owl drafts a translation from the pop-up as it is in this editor; a person
// reads it and Save keeps it.
const T_WORDS = { heading: [['text', 'Heading']], text: [['text', 'Text', true]], button: [['label', 'Button']], signup: [['label', 'Sign-up button']], image: [['alt', 'Image description']], countdown: [['label', 'Countdown label'], ['after', 'After the countdown', true]] };
function PromoLanguages({ draft, setDraft, entityId, apiScope }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState('');
  const tr = draft.translations || {};
  const main = draft.defaultLang || 'en';
  const setTr = (lang, v) => setDraft((d) => ({ ...d, translations: { ...(d.translations || {}), [lang]: v } }));
  const translate = async (lang) => {
    if (Object.keys(tr[lang] || {}).length && !window.confirm(`Replace the ${LANGS[lang]} wording with a fresh translation from the Owl?`)) return;
    setBusy(lang); setErr('');
    try {
      const r = await api.sitePromoTranslate(entityId, apiScope, { to: lang, promo: draft });
      if (r.error) setErr(r.error); else { setTr(lang, { ...(tr[lang] || {}), ...(r.translation || {}), blocks: mergeBlocks(tr[lang], r.translation) }); setOpen(lang); }
    } catch (e) { setErr(String(e.message || e)); }
    setBusy('');
  };
  const add = (lang) => { if (!lang) return; setTr(lang, {}); translate(lang); };
  const remove = (lang) => setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[lang]; return { ...d, translations: t }; });
  return (
    <div style={subRow}>
      <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Main language (the wording above is written in this)
        <select style={input} value={main} onChange={(e) => { const v = e.target.value; setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[v]; return { ...d, defaultLang: v, translations: t }; }); }}>
          {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <div style={{ ...hint, margin: '4px 0 0' }}>
        Each visitor sees the pop-up in their browser’s language when you have added it here, otherwise in the main
        language. Your website doesn’t need a language switcher. A sign-up form in the pop-up uses the same language when
        the form has that translation too.
      </div>
      {Object.keys(tr).map((lang) => (
        <div key={lang} style={{ ...subRow, background: 'var(--card)' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ flex: '1 1 120px' }}>{LANGS[lang] || lang}</b>
            <button type="button" style={miniBtn} disabled={!!busy} onClick={() => translate(lang)}>{busy === lang ? 'Translating…' : 'Translate with the Owl'}</button>
            <button type="button" style={miniBtn} onClick={() => setOpen(open === lang ? '' : lang)}>{open === lang ? 'Close' : 'Review'}</button>
            <button type="button" style={miniBtn} onClick={() => remove(lang)}>Remove</button>
          </div>
          {open === lang && <TranslationFields blocks={draft.blocks || []} t={tr[lang] || {}} onChange={(v) => setTr(lang, v)} entityId={entityId} apiScope={apiScope} />}
        </div>
      ))}
      <select style={{ ...input, marginTop: 8 }} value="" disabled={!!busy} onChange={(e) => add(e.target.value)} aria-label="Add a language">
        <option value="">{busy ? 'Translating…' : '+ Add a language (the Owl translates it for you to check)…'}</option>
        {Object.entries(LANGS).filter(([k]) => k !== main && !tr[k]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}
// A fresh Owl draft replaces the words but keeps a per-language picture or HTML
// someone already set.
function mergeBlocks(prev, next) {
  const out = { ...((next && next.blocks) || {}) };
  for (const [id, v] of Object.entries((prev && prev.blocks) || {})) {
    const keep = {}; if (v.src) keep.src = v.src; if (v.html) keep.html = v.html;
    if (Object.keys(keep).length) out[id] = { ...(out[id] || {}), ...keep };
  }
  return out;
}
function TranslationFields({ blocks, t, onChange, entityId, apiScope }) {
  const [up, setUp] = useState('');
  const [err, setErr] = useState('');
  const setB = (id, k, v) => onChange({ ...t, blocks: { ...(t.blocks || {}), [id]: { ...((t.blocks || {})[id] || {}), [k]: v } } });
  const val = (id, k) => ((t.blocks || {})[id] || {})[k] || '';
  const upload = async (id, file) => {
    if (!file) return;
    setUp(id); setErr('');
    try { const r = await api.sitePromoImage(entityId, apiScope, await toDataUrl(file)); if (r.error) setErr(r.error); else setB(id, 'src', r.src); }
    catch (e) { setErr(String(e.message || e)); }
    setUp('');
  };
  const box = (lbl, value, onSet, { area, ph, mono } = {}) => (
    <label style={{ ...label, fontWeight: 600 }}>{lbl}
      {area ? <textarea style={{ ...input, minHeight: mono ? 120 : 56, ...(mono ? { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 } : {}) }} value={value || ''} placeholder={ph || ''} onChange={(e) => onSet(e.target.value)} />
        : <input style={input} value={value || ''} placeholder={ph || ''} onChange={(e) => onSet(e.target.value)} />}
    </label>
  );
  return (
    <div>
      <div style={{ ...hint, marginTop: 6 }}>The grey text in each box is your main-language wording. A blank box falls back to it.</div>
      {blocks.map((b) => (
        <div key={b.id}>
          {(T_WORDS[b.type] || []).filter(([k]) => !(b.type === 'signup' && b.mode === 'inline' && k === 'label')).map(([k, lbl, area]) => (
            <div key={k}>{box(lbl, val(b.id, k), (v) => setB(b.id, k, v), { area, ph: b[k] || '' })}</div>
          ))}
          {b.type === 'image' && (
            <div>
              {box('A different image for this language (optional, e.g. artwork with words on it)', val(b.id, 'src'), (v) => setB(b.id, 'src', v), { ph: 'Same image as the main language' })}
              <label style={{ ...miniBtn, display: 'inline-flex', alignItems: 'center', marginTop: 4 }}>
                {up === b.id ? 'Uploading…' : 'Upload image'}
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={(e) => { upload(b.id, e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
          )}
          {b.type === 'html' && box('Your own HTML in this language (optional)', val(b.id, 'html'), (v) => setB(b.id, 'html', v), { area: true, mono: true, ph: 'Blank = the same HTML as the main language' })}
        </div>
      ))}
      {blocks.some((b) => b.type === 'signup' && b.mode !== 'inline') && box('“Back” link on the sign-up form', (t.ui || {}).back, (v) => onChange({ ...t, ui: { ...(t.ui || {}), back: v } }), { ph: 'Back' })}
      <div style={hint}>Countdown units (days, hours…) are translated automatically. Translate the sign-up form itself in Settings → Newsletter sign-ups.</div>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

// A Sign-up form block shows one of the client's newsletter sign-up forms.
// The form owns the fields, consent boxes, confirmation email and — the point —
// its own segment in Engage → Segments, so everyone who joins through the
// pop-up is ready to send to. "Create one for this pop-up" makes a form (and
// so a segment) named after the pop-up, with the heading left to the pop-up.
function SignupFields({ b, upd, entityId, apiScope, signup, promoName }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const forms = signup?.forms || [];
  const picked = forms.find((f) => f.key === b.form);
  const create = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.newsletterFormCreate(entityId, apiScope, { name: `Pop-up: ${promoName || 'sign-up'}`.slice(0, 120), showHeading: false, origin: 'popup' });
      if (r.error) throw new Error(r.error);
      const made = (r.forms || []).find((f) => f.id === r.id);
      if (made) upd({ form: made.siteKey });
      if (signup?.reload) await signup.reload();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };
  if (!signup?.on) {
    return (
      <div style={{ ...hint, margin: 0 }}>
        Sign-up forms aren’t switched on for this account yet. Ask Howler to turn on <b>Newsletter sign-up forms</b>, then
        come back here to add one.
      </div>
    );
  }
  return (
    <div>
      <select style={input} value={b.form || ''} onChange={(e) => upd({ form: e.target.value })} aria-label="Which sign-up form">
        <option value="">Pick a sign-up form…</option>
        {forms.map((f) => <option key={f.key} value={f.key}>{f.name}{f.status === 'paused' ? ' (paused)' : ''}</option>)}
      </select>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
        <button type="button" style={miniBtn} disabled={busy} onClick={create}>{busy ? 'Creating…' : '+ Create a sign-up form for this pop-up'}</button>
      </div>
      <label style={{ ...label, fontWeight: 400 }}>How it shows
        <select style={input} value={b.mode === 'inline' ? 'inline' : 'button'} onChange={(e) => upd({ mode: e.target.value })}>
          <option value="button">A button that opens the full form in the pop-up</option>
          <option value="inline">The form right in the pop-up</option>
        </select>
      </label>
      {b.mode !== 'inline' && (
        <input style={{ ...input, marginTop: 6 }} value={b.label ?? ''} placeholder="Button text, e.g. Register" maxLength={60} onChange={(e) => upd({ label: e.target.value })} aria-label="Button text" />
      )}
      {picked && <AfterSignup form={picked} entityId={entityId} apiScope={apiScope} reload={signup?.reload} />}
      {picked && picked.status === 'paused' && <div style={errBox}>This form is paused, so the pop-up won’t show it. Resume it in Settings → Newsletter sign-ups.</div>}
      {b.mode === 'inline' && <label style={{ ...chkRow, marginTop: 6 }}><input type="checkbox" style={chk} checked={!!b.compact} onChange={(e) => upd({ compact: e.target.checked })} /> Just the email box and button (plus the consent boxes)</label>}
      {b.mode === 'inline' && !b.compact && <label style={chkRow}><input type="checkbox" style={chk} checked={!!b.ownHeading} onChange={(e) => upd({ ownHeading: e.target.checked })} /> Also show the form’s own heading and intro line</label>}
      <div style={{ ...hint, margin: '4px 0 0' }}>
        {b.mode !== 'inline' && 'Tapping the button turns the pop-up into the form: your heading, then the form’s fields (first name, last name and email by default), consent boxes and submit, with a Back link. '}
        Everyone who signs up is added to that form’s list and to its segment in Engage → Segments (a form made here files it in the Website pop-ups folder),
        ready for a campaign. Its fields, consent boxes and confirmation email are set in Settings → Newsletter sign-ups.
        Someone who signs up through the pop-up doesn’t see it again. Sign-ups made from Preview are real, so use a test address.
      </div>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

// What happens after someone signs up — the FORM's settings (a form can be in
// several places), edited here so the pop-up can be finished in one go.
function AfterSignup({ form, entityId, apiScope, reload }) {
  const [dbl, setDbl] = useState(form.doubleOptIn);
  const [msg, setMsg] = useState(form.successMessage || '');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => { setDbl(form.doubleOptIn); setMsg(form.successMessage || ''); setNote(''); }, [form.id, form.doubleOptIn, form.successMessage]);
  const dirty = dbl !== form.doubleOptIn || msg !== (form.successMessage || '');
  const save = async () => {
    setBusy(true); setNote('');
    try {
      const r = await api.newsletterFormUpdate(entityId, apiScope, form.id, { doubleOptIn: dbl, successMessage: msg });
      if (r.error) setNote(r.error); else { setNote('Saved.'); if (reload) await reload(); }
    } catch (e) { setNote(String(e.message || e)); }
    setBusy(false);
  };
  return (
    <div style={{ ...subRow, background: 'var(--card)' }}>
      <b style={{ fontSize: 12.5 }}>After they sign up</b>
      <label style={{ ...chkRow, marginTop: 6 }}><input type="checkbox" style={chk} checked={dbl} onChange={(e) => setDbl(e.target.checked)} /> Ask them to confirm by email first (double opt-in)</label>
      <div style={{ ...hint, margin: '0 0 4px' }}>
        {dbl ? 'They get an email with a confirm button, and only join the list and segment once they tap it. Recommended, and expected in the EU.'
          : 'They join the list and segment as soon as they submit. No email is sent.'}
      </div>
      <label style={{ ...label, fontWeight: 400, marginTop: 4 }}>Message shown in the pop-up after they submit
        <textarea style={{ ...input, minHeight: 64 }} value={msg} maxLength={300} onChange={(e) => setMsg(e.target.value)}
          placeholder={dbl ? 'Almost there!\nCheck your inbox and tap the link in our email to confirm your subscription.' : 'Thank you!\nYou have successfully joined our subscriber list.'} />
      </label>
      <div style={{ ...hint, margin: '0 0 4px' }}>The first line shows in bold. Blank uses the wording in grey. This changes the form “{form.name}” everywhere it is used.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={{ ...miniBtn, ...(dirty ? primary : {}) }} disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save these form settings'}</button>
        {note && <span style={{ fontSize: 12, color: note === 'Saved.' ? 'var(--muted)' : 'var(--error)' }}>{note}</span>}
      </div>
    </div>
  );
}

function Colour({ label: l, value, onChange, fallback, blankLabel }) {
  const ok = /^#[0-9a-f]{6}$/i.test(value || '');
  return (
    <label style={{ ...chkRow, alignItems: 'center', minHeight: 44, margin: '4px 0' }}>
      <span style={{ flex: '1 1 120px' }}>{l}</span>
      <input type="color" style={{ width: 48, height: 40, border: '1px solid var(--hairline)', borderRadius: 9, background: 'var(--bg)', padding: 2 }} value={ok ? value : fallback} onChange={(e) => onChange(e.target.value)} />
      <span style={{ width: 120, fontSize: 12, color: 'var(--muted)' }}>{ok ? value : blankLabel || fallback}</span>
      {blankLabel && ok && <button type="button" style={miniBtn} onClick={() => onChange('')}>Use branding</button>}
    </label>
  );
}
function Num({ label: l, value, max, unit, onChange }) {
  return (
    <label style={{ ...chkRow, alignItems: 'center', minHeight: 44, margin: '4px 0' }}>
      <span style={{ flex: '1 1 180px' }}>{l}</span>
      <input type="number" min={0} max={max} inputMode="numeric" style={{ ...input, width: 90, flex: 'none' }} value={value ?? 0}
        onChange={(e) => onChange(Math.max(0, Math.min(max, Number(e.target.value) || 0)))} aria-label={l} />
      {unit && <span style={{ width: 70, color: 'var(--muted)' }}>{unit}</span>}
    </label>
  );
}
// Re-encode through a canvas (raster only, at most 1600px wide), the way every
// Pulse uploader does — the server only ever serves real images back.
function toDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) return reject(new Error('Upload a PNG, JPEG, GIF or WebP image.'));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1600 / img.naturalWidth);
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(/png|gif|webp/i.test(file.type) ? c.toDataURL('image/png') : c.toDataURL('image/jpeg', 0.86));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as an image.')); };
    img.src = url;
  });
}
const pad = (n) => String(n).padStart(2, '0');
const toLocal = (iso) => { const d = new Date(iso || ''); return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const fromLocal = (v) => { const d = new Date(v || ''); return Number.isNaN(d.getTime()) ? '' : d.toISOString(); };
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
function openWhen(t) {
  const parts = [t.delay > 0 ? `after ${t.delay} second${t.delay === 1 ? '' : 's'}` : (!t.scroll && !t.exit ? 'as soon as the page opens' : ''),
    t.scroll > 0 && `once ${t.scroll}% of the page is scrolled`, t.exit && 'when they move to leave'].filter(Boolean);
  return `Opens ${parts.join(', or ')}${t.pageviews > 1 ? `, from the ${ordinal(t.pageviews)} page a visitor opens on your site` : ''}.`;
}
const ordinal = (n) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;
function summary(p) {
  const bits = [p.trigger.manual ? `opens from #${p.linkHash || 'promo'} links` : openWhen(p.trigger).replace(/^Opens /, 'opens ').replace(/\.$/, ''),
    FREQ_LABEL[p.frequency.mode].toLowerCase() + (p.frequency.mode === 'days' ? ` (${p.frequency.days})` : ''),
    (p.targeting.include || []).length ? `on ${p.targeting.include.join(', ')}` : 'every page',
    p.targeting.device !== 'all' && (p.targeting.device === 'mobile' ? 'phones only' : 'computers only'),
    p.startsAt && `from ${fmt(p.startsAt)}`, p.endsAt && `until ${fmt(p.endsAt)}`].filter(Boolean);
  const s = bits.join(' · ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, margin: '12px 0 4px' };
const chkRow = { display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400, fontSize: 13, lineHeight: 1.45, minHeight: 32 };
const chk = { width: 18, height: 18, marginTop: 1, flex: 'none' };
const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' };
const primary = { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent', fontWeight: 700 };
const panel = { marginTop: 12, paddingTop: 4, borderTop: '1px solid var(--hairline)' };
const pill = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
const subRow = { border: '1px solid var(--hairline)', borderRadius: 9, padding: 8, marginTop: 6, background: 'var(--bg)' };
const miniBtn = { ...btn, minHeight: 36, padding: '6px 10px' };
const code = { fontSize: 12, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 9, padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '0 0 8px' };
const errBox = { fontSize: 12.5, color: 'var(--error)', marginTop: 8, lineHeight: 1.5 };
const okBox = { fontSize: 12.5, marginTop: 10, padding: 10, borderRadius: 9, background: 'var(--bg)', border: '1px solid var(--hairline)', lineHeight: 1.5 };
